import { useDotLottie, LoadEvent } from '@lottiefiles/dotlottie-react';
import React, { useEffect, useMemo, useState } from 'react';
import PickProject from './a-picking-projects.svg?react';
import PickResource from './a-picking-resources.svg?react';
import SelectingView from './a-selecting-your-view-2.svg?react';
import { Icon, ITextProps, ScrollablePane, Stack, Text, useTheme } from '@fluentui/react';
import { Link } from 'react-router-dom';
import ReactMarkdown from 'react-markdown';
import remarkGfm from 'remark-gfm';
import rehypeRaw from 'rehype-raw';
import { useStore } from '../../context/store';
import { useUISettings } from '../../context/network/http/QueryProvider/queries/UISettings';
import { Recent } from '../../context/contexts/recentlyViewsContext';
import defaultText from './default-text.md?raw';

export const LandingPageInner = ({ markdown, useDefaultMarkdown }: { markdown: string; useDefaultMarkdown?: boolean }) => {
    const { data: uISettings } = useUISettings();
    const theme = useTheme();
    const recentHistory = useStore(store => store.recentHistory);
    return (
        <Stack styles={{ root: { backgroundColor: theme.semanticColors.bodyBackgroundChecked, height: '100%', padding: 20 } }}>
            <Stack
                styles={{
                    root: {
                        borderRadius: 5,
                        backgroundColor: theme.semanticColors.bodyBackground,
                        height: '100%',
                        position: 'relative',
                    },
                }}
            >
                <ScrollablePane>
                    <Stack horizontal horizontalAlign="center" styles={{ root: { paddingTop: 50 } }}>
                        <Stack horizontalAlign="center" tokens={{ maxWidth: 1000 }}>
                            <Stack tokens={{ padding: '0 0 50px 0' }} horizontalAlign="center">
                                <Text variant="xLarge" as="p">
                                    Welcome, {uISettings.resource.name}!
                                </Text>
                                <Text>Here's your quick guide on how to get started with Team Planner.</Text>
                                <Text>Hover your mouse over a guide to see the animations.</Text>
                            </Stack>
                            <Stack tokens={{ childrenGap: 50 }}>
                                <Stack horizontalAlign="center">
                                    <div
                                        style={{
                                            height: '100%',
                                            width: '100%',
                                            display: 'grid',
                                            gridTemplateColumns: '1fr 1fr 1fr',
                                            columnGap: 30,
                                            rowGap: 50,
                                            maxWidth: 1000,
                                        }}
                                    >
                                        <AnimatedCard
                                            src="/images/a-selecting-your-view-2.json"
                                            title="Selecting your view"
                                            description="Depending on your task, select the appropriate view in the top bar to either request, allocate or plan in customized overviews."
                                        >
                                            <SelectingView height={'100%'} width={'100%'} />
                                        </AnimatedCard>
                                        <AnimatedCard
                                            src="/images/a-picking-projects.json"
                                            title="Picking projects"
                                            description="Pick your project from the panel on the left hand side of the screen. Click on a project or drag it to your view to open it."
                                        >
                                            <PickProject height={'100%'} width={'100%'} />
                                        </AnimatedCard>
                                        <AnimatedCard
                                            src="/images/a-picking-resources.json"
                                            title="Picking resources"
                                            description="Pick your resource from the panel on the right hand side of the screen. Click on the Add icon or drag the resource to your view to open it."
                                        >
                                            <PickResource height={'100%'} width={'100%'} />
                                        </AnimatedCard>
                                    </div>
                                </Stack>
                                <Stack horizontal tokens={{ childrenGap: 20 }}>
                                    <Stack styles={{ root: { width: '100%' } }}>
                                        {recentHistory && (
                                            <>
                                                <Text variant="xLarge" as="p">
                                                    Recently used
                                                </Text>
                                                {recentHistory?.map((point, i) => {
                                                    return <RecentPoint key={i} point={point} />;
                                                })}
                                            </>
                                        )}
                                    </Stack>
                                    <Stack styles={{ root: { width: '100%' } }} horizontalAlign="end">
                                        <ReactMarkdown
                                            className="markdown-body"
                                            remarkPlugins={[remarkGfm as any]}
                                            rehypePlugins={[rehypeRaw as any]}
                                            components={components}
                                        >
                                            {useDefaultMarkdown ? defaultText : markdown}
                                        </ReactMarkdown>
                                    </Stack>
                                </Stack>
                            </Stack>
                        </Stack>
                    </Stack>
                </ScrollablePane>
            </Stack>
        </Stack>
    );
};

export const LandingPage = () => {
    const { data: uiSettings } = useUISettings();
    return (
        <LandingPageInner
            markdown={
                uiSettings?.settings?.customFrontPageSetting?.customFrontPageEnabled
                    ? uiSettings?.settings?.customFrontPageSetting?.customFrontPageMarkdown
                    : defaultText
            }
        />
    );
};

const components: ReactMarkdown.ReactMarkdownOptions['components'] = {
    a: (props: React.DetailedHTMLProps<React.AnchorHTMLAttributes<HTMLAnchorElement>, HTMLAnchorElement>) => <a {...props} target="_blank" />,
    h1: (props: React.DetailedHTMLProps<React.HTMLAttributes<HTMLHeadingElement>, HTMLHeadingElement>) => (
        <Text variant="xLarge" styles={headlineStyles}>
            {props.children}
        </Text>
    ),
    h2: (props: React.DetailedHTMLProps<React.HTMLAttributes<HTMLHeadingElement>, HTMLHeadingElement>) => (
        <Text variant="large" styles={headlineStyles}>
            {props.children}
        </Text>
    ),
    h3: (props: React.DetailedHTMLProps<React.HTMLAttributes<HTMLHeadingElement>, HTMLHeadingElement>) => (
        <Text variant="mediumPlus" styles={headlineStyles}>
            {props.children}
        </Text>
    ),
    p: (props: React.DetailedHTMLProps<React.HTMLAttributes<HTMLParagraphElement>, HTMLParagraphElement>) => (
        <Text as={'p'} styles={headlineStyles}>
            {props.children}
        </Text>
    ),
};

const headlineStyles: ITextProps['styles'] = {
    root: { display: 'block', marginBottom: '1rem' },
};

const RecentPoint = ({ point: { text, url, description, viewName } }: { point: Recent }) => {
    const iconName = useMemo(() => {
        switch (viewName) {
            case 'allocation':
                return 'ReminderGroup';
            case 'request':
                return 'Family';
            case 'resource-planner':
                return 'AddGroup';
            case 'project-planner':
                return 'PlannerLogo';
        }
    }, [viewName]);
    return (
        <Stack horizontal tokens={{ childrenGap: 5 }}>
            <Icon iconName={iconName} />{' '}
            <Link to={url}>
                {text} {viewName && `- ${viewName}`}
            </Link>
        </Stack>
    );
};

const AnimatedCard = ({
    description,
    src,
    title,
    children,
}: {
    src: string;
    children?: React.ReactNode;
    title: React.ReactNode;
    description: React.ReactNode;
}) => {
    const { DotLottieComponent, dotLottie } = useDotLottie({
        src,
        playOnHover: false,
        loop: true,
        autoplay: false,
        renderConfig: { autoResize: true, freezeOnOffscreen: true },
    });

    const [loadedAnimation, setLoadedAnimation] = useState(false);

    useEffect(() => {
        if (dotLottie) {
            const handleLoad = (e: LoadEvent) => {
                setLoadedAnimation(true);
            };
            dotLottie.addEventListener('load', handleLoad);
            return () => {
                dotLottie.removeEventListener('load', handleLoad);
            };
        }
    }, [dotLottie]);

    return (
        <Stack
            //
            tokens={{ childrenGap: 10 }}
            onMouseLeave={() => dotLottie.stop()}
            onMouseEnter={() => dotLottie.play()}
        >
            <div style={{ width: '100%', aspectRatio: '16 / 8.3', borderRadius: 5, border: `1px solid rgba(0,0,0,0.2)`, overflow: 'hidden' }}>
                <div style={{ display: loadedAnimation ? 'block' : 'none', height: '100%', width: '100%' }}>
                    <DotLottieComponent />
                </div>
                {!loadedAnimation && <div style={{ height: '100%', width: '100%' }}>{children}</div>}
            </div>
            <Stack tokens={{ childrenGap: 10 }}>
                <Text>
                    <strong>{title}</strong>
                </Text>
                <Text variant="smallPlus">{description}</Text>
            </Stack>
        </Stack>
    );
};
